import React, { useState, useEffect } from 'react'
import Card from 'core-system/Card'
import { ArrowUp, ArrowDown, Star, Calendar, Filter } from 'lucide-react'
import CountUp from 'react-countup'
import * as Tooltip from '@radix-ui/react-tooltip'
import {
  PieChart as RechartsPieChart,
  Pie as RechartsPie,
  Cell as RechartsCell,
  ResponsiveContainer as RechartsResponsiveContainer,
  BarChart as RechartsBarChart,
  Bar as RechartsBar,
  XAxis as RechartsXAxis,
  YAxis as RechartsYAxis,
  CartesianGrid as RechartsCartesianGrid,
  Tooltip as RechartsTooltip,
} from 'recharts'
import styled from 'styled-components'

const MainContent = styled.main`
  flex: 1;
  overflow: auto;
`

const ContentContainer = styled.div`
  padding: 2rem;
`

const TitleSection = styled.div`
  margin-bottom: 2rem;
`

const PageTitle = styled.h2`
  font-size: 1.875rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 0.5rem;
`

const PageDescription = styled.p`
  font-size: 1rem;
  color: #4b5563;
`

const GridContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin-bottom: 2rem;
`

const KPICard = styled(Card)<{ $delay?: string }>`
  padding: 1.5rem;
  background-color: white;
  transition: all 0.2s;
  cursor: pointer;
  flex: 1 1 200px;
  min-width: 200px;
  max-width: 100%;

  &:hover {
    transform: scale(1.02);
    box-shadow:
      0 10px 15px -3px rgba(0, 0, 0, 0.1),
      0 4px 6px -2px rgba(0, 0, 0, 0.05);
  }
`

const KPILabel = styled.div`
  font-size: 0.875rem;
  color: #4b5563;
  margin-bottom: 0.5rem;
`

const KPIValueContainer = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
`

const KPIValue = styled.span`
  font-size: 2.25rem;
  font-weight: 700;
  color: #111827;
`

const KPIUnit = styled.span`
  font-size: 1.125rem;
  color: #4b5563;
`

const ChangeIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.875rem;
  color: #16a34a;
  margin-bottom: 0.75rem;
`

const ProgressBar = styled.div`
  height: 0.25rem;
  background-color: #f3f4f6;
  border-radius: 9999px;
  overflow: hidden;
`

const ProgressFill = styled.div<{
  $width: string
  $color: string
  $delay?: string
}>`
  height: 100%;
  background-color: ${(props) => props.$color};
  border-radius: 9999px;
  width: ${(props) => props.$width};
`

const SkeletonBar = styled.div<{
  $width: string
  $height: string
  $marginBottom?: string
}>`
  height: ${(props) => props.$height};
  background-color: #e5e7eb;
  border-radius: 0.25rem;
  width: ${(props) => props.$width};
  margin-bottom: ${(props) => props.$marginBottom || '0'};
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
`

const SkeletonCardContent = styled.div`
  padding: 1.5rem;
`

const PerformanceCard = styled(Card)`
  padding: 2rem;
  margin-bottom: 2rem;
  background-color: white;
`

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
`

const CardTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
`

const CardActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`

const DateButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  background: white;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: #f9fafb;
  }
`

const FilterButton = styled.button`
  padding: 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  background: white;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: #f9fafb;
  }
`

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  margin-bottom: 2rem;
`

const ChartSection = styled.div``

const ChartTitle = styled.h4`
  font-size: 1rem;
  font-weight: 500;
  color: #111827;
  margin-bottom: 1rem;
`

const DonutChartContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
`

const DonutWrapper = styled.div`
  position: relative;
  width: 12rem;
  height: 12rem;
`

const DonutCenter = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  text-align: center;
`

const DonutCenterValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: #111827;
`

const DonutCenterLabel = styled.div`
  font-size: 0.75rem;
  color: #4b5563;
`

const ChannelList = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`

const ChannelItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const ChannelInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const ChannelDot = styled.div<{ $color: string }>`
  width: 0.75rem;
  height: 0.75rem;
  border-radius: 9999px;
  background-color: ${(props) => props.$color};
`

const ChannelName = styled.span`
  font-size: 0.875rem;
  color: #374151;
`

const ChannelStats = styled.div`
  text-align: right;
`

const ChannelPercentage = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: #111827;
`

const ChannelValue = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
`

const BarChartContainer = styled.div`
  height: 14rem;
`

const FunnelSection = styled.div`
  margin-top: 2rem;
`

const FunnelTitle = styled.h4`
  font-size: 1rem;
  font-weight: 500;
  color: #111827;
  margin-bottom: 1.5rem;
`

const FunnelContainer = styled.div`
  background: linear-gradient(
    to bottom right,
    #f9fafb,
    rgba(249, 250, 251, 0.5)
  );
  border-radius: 0.75rem;
  padding: 2rem;
  border: 1px solid #e5e7eb;
`

const StyledSVG = styled.svg`
  overflow: visible;
`

const Performance = React.memo(() => {
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 1000)
    return () => clearTimeout(timer)
  }, [])

  const SkeletonCard = () => (
    <Card>
      <SkeletonCardContent>
        <SkeletonBar $width='50%' $height='1rem' $marginBottom='1rem' />
        <SkeletonBar $width='75%' $height='2.5rem' $marginBottom='0.5rem' />
        <SkeletonBar $width='33%' $height='1rem' />
      </SkeletonCardContent>
    </Card>
  )

  // Channel data for donut chart
  const channelData = [
    { name: 'Email', value: 1840, percentage: 45, color: '#5C1631' },
    { name: 'Slack Bot', value: 1440, percentage: 35, color: '#9B7EAC' },
    { name: 'Manual Requests', value: 780, percentage: 20, color: '#7B8794' },
  ]

  // Weekly resolution data for stacked bar chart
  const weeklyData = [
    { week: 'W1', autoResolved: 426, escalated: 94 },
    { week: 'W2', autoResolved: 476, escalated: 104 },
    { week: 'W3', autoResolved: 484, escalated: 106 },
    { week: 'W4', autoResolved: 525, escalated: 115 },
    { week: 'W5', autoResolved: 508, escalated: 112 },
    { week: 'W6', autoResolved: 459, escalated: 101 },
    { week: 'W7', autoResolved: 451, escalated: 99 },
  ]

  return (
    <Tooltip.Provider delayDuration={200}>
      <div>
        {/* Main Content */}
        <MainContent>
          <ContentContainer>
            <TitleSection>
              <PageTitle>Performance Dashboard</PageTitle>
              <PageDescription>
                Monitor Fleet AI Concierge performance across all support
                channels and resolutions.
              </PageDescription>
            </TitleSection>

            {/* KPI Summary Row */}
            {isLoading ? (
              <GridContainer>
                <SkeletonCard />
                <SkeletonCard />
                <SkeletonCard />
                <SkeletonCard />
                <SkeletonCard />
              </GridContainer>
            ) : (
              <GridContainer>
                <KPICard $delay='0ms'>
                  <KPILabel>Questions Handled</KPILabel>
                  <KPIValueContainer>
                    <KPIValue>
                      <CountUp end={4060} duration={1.8} separator=',' />
                    </KPIValue>
                  </KPIValueContainer>
                  <ChangeIndicator>
                    <ArrowUp style={{ width: '1rem', height: '1rem' }} />
                    <span>18%</span>
                  </ChangeIndicator>
                  <ProgressBar>
                    <ProgressFill $width='82%' $color='#5C1631' />
                  </ProgressBar>
                </KPICard>

                <KPICard $delay='100ms'>
                  <KPILabel>Auto-Resolved Rate</KPILabel>
                  <KPIValueContainer>
                    <KPIValue>
                      <CountUp end={82} duration={1.8} suffix='%' />
                    </KPIValue>
                  </KPIValueContainer>
                  <ChangeIndicator>
                    <ArrowUp style={{ width: '1rem', height: '1rem' }} />
                    <span>5%</span>
                  </ChangeIndicator>
                  <ProgressBar>
                    <ProgressFill $width='82%' $color='#5C1631' />
                  </ProgressBar>
                </KPICard>

                <KPICard $delay='200ms'>
                  <KPILabel>Human Escalations</KPILabel>
                  <KPIValueContainer>
                    <KPIValue>
                      <CountUp end={730} duration={1.8} />
                    </KPIValue>
                  </KPIValueContainer>
                  <ChangeIndicator>
                    <ArrowDown style={{ width: '1rem', height: '1rem' }} />
                    <span>7%</span>
                  </ChangeIndicator>
                  <ProgressBar>
                    <ProgressFill $width='18%' $color='#BFA6CC' />
                  </ProgressBar>
                </KPICard>

                <KPICard $delay='300ms'>
                  <KPILabel>Avg Response Time</KPILabel>
                  <KPIValueContainer>
                    <KPIValue>
                      <CountUp end={4.3} duration={1.8} decimals={1} />
                    </KPIValue>
                    <KPIUnit>s</KPIUnit>
                  </KPIValueContainer>
                  <ChangeIndicator>
                    <ArrowDown style={{ width: '1rem', height: '1rem' }} />
                    <span>10%</span>
                  </ChangeIndicator>
                  <ProgressBar>
                    <ProgressFill $width='43%' $color='#5C1631' />
                  </ProgressBar>
                </KPICard>

                <KPICard $delay='400ms'>
                  <KPILabel>Employee CSAT</KPILabel>
                  <KPIValueContainer>
                    <KPIValue>
                      <CountUp end={4.7} duration={1.8} decimals={1} />
                    </KPIValue>
                    <Star
                      style={{
                        width: '1.25rem',
                        height: '1.25rem',
                        color: '#fbbf24',
                        fill: '#fbbf24',
                      }}
                    />
                  </KPIValueContainer>
                  <ChangeIndicator>
                    <ArrowUp style={{ width: '1rem', height: '1rem' }} />
                    <span>0.2</span>
                  </ChangeIndicator>
                  <ProgressBar>
                    <ProgressFill $width='94%' $color='#5C1631' />
                  </ProgressBar>
                </KPICard>
              </GridContainer>
            )}

            {/* Performance Visualization Section */}
            <PerformanceCard>
              <CardHeader>
                <CardTitle>Performance</CardTitle>
                <CardActions>
                  <DateButton>
                    <Calendar
                      style={{
                        width: '1rem',
                        height: '1rem',
                        color: '#4b5563',
                      }}
                    />
                    <span style={{ color: '#374151' }}>
                      Apr 1, 2025 - May 15, 2025
                    </span>
                  </DateButton>
                  <FilterButton>
                    <Filter
                      style={{
                        width: '1rem',
                        height: '1rem',
                        color: '#4b5563',
                      }}
                    />
                  </FilterButton>
                </CardActions>
              </CardHeader>

              {/* Channel Breakdown & Resolution Volume */}
              <ChartsGrid>
                {/* Channel Breakdown Donut Chart */}
                <ChartSection>
                  <ChartTitle>Channel Breakdown</ChartTitle>
                  <DonutChartContainer>
                    <DonutWrapper>
                      <RechartsResponsiveContainer width='100%' height='100%'>
                        <RechartsPieChart>
                          <RechartsPie
                            data={channelData}
                            cx='50%'
                            cy='50%'
                            innerRadius={60}
                            outerRadius={85}
                            paddingAngle={2}
                            dataKey='value'
                            animationBegin={500}
                            animationDuration={1000}
                            stroke='#fff'
                            strokeWidth={2}
                          >
                            {channelData.map((entry, index) => (
                              <RechartsCell
                                key={`cell-${index}`}
                                fill={entry.color}
                                stroke='#fff'
                                strokeWidth={2}
                                style={{ fill: entry.color }}
                              />
                            ))}
                          </RechartsPie>
                        </RechartsPieChart>
                      </RechartsResponsiveContainer>
                      <DonutCenter>
                        <div>
                          <DonutCenterValue>
                            <CountUp end={4060} duration={1.8} separator=',' />
                          </DonutCenterValue>
                          <DonutCenterLabel>Total Requests</DonutCenterLabel>
                        </div>
                      </DonutCenter>
                    </DonutWrapper>
                    <ChannelList>
                      {channelData.map((channel) => (
                        <ChannelItem key={channel.name}>
                          <ChannelInfo>
                            <ChannelDot $color={channel.color} />
                            <ChannelName>{channel.name}</ChannelName>
                          </ChannelInfo>
                          <ChannelStats>
                            <ChannelPercentage>
                              {channel.percentage}%
                            </ChannelPercentage>
                            <ChannelValue>
                              <CountUp
                                end={channel.value}
                                duration={1.8}
                                separator=','
                              />
                            </ChannelValue>
                          </ChannelStats>
                        </ChannelItem>
                      ))}
                    </ChannelList>
                  </DonutChartContainer>
                </ChartSection>

                {/* Resolution Volume Stacked Bar Chart */}
                <ChartSection>
                  <ChartTitle>Resolution Volume</ChartTitle>
                  <BarChartContainer>
                    <RechartsResponsiveContainer width='100%' height='100%'>
                      <RechartsBarChart data={weeklyData}>
                        <RechartsCartesianGrid
                          strokeDasharray='3 3'
                          stroke='#E5E7EB'
                          vertical={false}
                        />
                        <RechartsXAxis
                          dataKey='week'
                          stroke='#6B7280'
                          fontSize={12}
                        />
                        <RechartsYAxis stroke='#6B7280' fontSize={12} />
                        <RechartsTooltip
                          contentStyle={{
                            backgroundColor: '#FFFFFF',
                            border: '1px solid #E5E7EB',
                            borderRadius: '8px',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                            padding: '12px',
                          }}
                          labelStyle={{
                            color: '#374151',
                            fontWeight: 600,
                            marginBottom: '4px',
                          }}
                          itemStyle={{
                            color: '#6B7280',
                            fontSize: '14px',
                          }}
                        />
                        <RechartsBar
                          dataKey='autoResolved'
                          stackId='a'
                          fill='#5C1631'
                          radius={[0, 0, 0, 0]}
                          name='Auto-Resolved'
                        />
                        <RechartsBar
                          dataKey='escalated'
                          stackId='a'
                          fill='#9B7EAC'
                          radius={[4, 4, 0, 0]}
                          name='Escalated'
                        />
                      </RechartsBarChart>
                    </RechartsResponsiveContainer>
                  </BarChartContainer>
                </ChartSection>
              </ChartsGrid>

              {/* Performance Funnel */}
              <FunnelSection>
                <FunnelTitle>Performance funnel</FunnelTitle>
                <FunnelContainer>
                  <StyledSVG width='100%' height='320' viewBox='0 0 1200 320'>
                    <defs>
                      <linearGradient
                        id='flowGradient1'
                        x1='0%'
                        y1='0%'
                        x2='100%'
                        y2='0%'
                      >
                        <stop
                          offset='0%'
                          stopColor='#3B82F6'
                          stopOpacity='0.3'
                        />
                        <stop
                          offset='100%'
                          stopColor='#3B82F6'
                          stopOpacity='0.1'
                        />
                      </linearGradient>
                      <linearGradient
                        id='flowGradient2'
                        x1='0%'
                        y1='0%'
                        x2='100%'
                        y2='0%'
                      >
                        <stop
                          offset='0%'
                          stopColor='#10B981'
                          stopOpacity='0.3'
                        />
                        <stop
                          offset='100%'
                          stopColor='#10B981'
                          stopOpacity='0.1'
                        />
                      </linearGradient>
                      <linearGradient
                        id='flowGradient3'
                        x1='0%'
                        y1='0%'
                        x2='100%'
                        y2='0%'
                      >
                        <stop
                          offset='0%'
                          stopColor='#6B7280'
                          stopOpacity='0.3'
                        />
                        <stop
                          offset='100%'
                          stopColor='#6B7280'
                          stopOpacity='0.1'
                        />
                      </linearGradient>
                    </defs>

                    {/* Stage 1: Input Channels */}
                    <rect
                      x='30'
                      y='20'
                      width='8'
                      height='110'
                      fill='#3B82F6'
                      rx='4'
                    />
                    <text
                      x='45'
                      y='35'
                      fontSize='11'
                      fill='#374151'
                      fontWeight='500'
                    >
                      EMAIL CONVERSATIONS
                    </text>
                    <text
                      x='45'
                      y='52'
                      fontSize='13'
                      fill='#1F2937'
                      fontWeight='700'
                    >
                      1,840
                    </text>

                    <rect
                      x='30'
                      y='140'
                      width='8'
                      height='85'
                      fill='#3B82F6'
                      rx='4'
                    />
                    <text
                      x='45'
                      y='155'
                      fontSize='11'
                      fill='#374151'
                      fontWeight='500'
                    >
                      SLACK CONVERSATIONS
                    </text>
                    <text
                      x='45'
                      y='172'
                      fontSize='13'
                      fill='#1F2937'
                      fontWeight='700'
                    >
                      1,440
                    </text>

                    <rect
                      x='30'
                      y='235'
                      width='8'
                      height='55'
                      fill='#3B82F6'
                      rx='4'
                    />
                    <text
                      x='45'
                      y='250'
                      fontSize='11'
                      fill='#374151'
                      fontWeight='500'
                    >
                      MANUAL REQUESTS
                    </text>
                    <text
                      x='45'
                      y='267'
                      fontSize='13'
                      fill='#1F2937'
                      fontWeight='700'
                    >
                      780
                    </text>

                    {/* Flow paths */}
                    <path
                      d='M 38 75 Q 200 75 320 100'
                      stroke='url(#flowGradient1)'
                      strokeWidth='55'
                      fill='none'
                      opacity='0.4'
                    />
                    <path
                      d='M 38 182 Q 200 182 320 140'
                      stroke='url(#flowGradient1)'
                      strokeWidth='42'
                      fill='none'
                      opacity='0.4'
                    />
                    <path
                      d='M 38 262 Q 200 262 320 170'
                      stroke='url(#flowGradient1)'
                      strokeWidth='27'
                      fill='none'
                      opacity='0.4'
                    />

                    {/* Stage 2: AI Involved */}
                    <rect
                      x='320'
                      y='40'
                      width='8'
                      height='200'
                      fill='#3B82F6'
                      rx='4'
                    />
                    <text
                      x='335'
                      y='55'
                      fontSize='11'
                      fill='#374151'
                      fontWeight='500'
                    >
                      AI INVOLVED
                    </text>
                    <text
                      x='335'
                      y='72'
                      fontSize='13'
                      fill='#1F2937'
                      fontWeight='700'
                    >
                      4,060
                    </text>

                    <path
                      d='M 328 100 Q 500 100 600 110'
                      stroke='url(#flowGradient2)'
                      strokeWidth='82'
                      fill='none'
                      opacity='0.4'
                    />
                    <path
                      d='M 328 180 Q 450 200 560 220'
                      stroke='url(#flowGradient3)'
                      strokeWidth='39'
                      fill='none'
                      opacity='0.3'
                    />
                    <path
                      d='M 328 210 Q 450 240 560 260'
                      stroke='url(#flowGradient3)'
                      strokeWidth='13'
                      fill='none'
                      opacity='0.3'
                    />

                    {/* Stage 3: Outcomes */}
                    <rect
                      x='600'
                      y='60'
                      width='8'
                      height='164'
                      fill='#10B981'
                      rx='4'
                    />
                    <text
                      x='615'
                      y='75'
                      fontSize='11'
                      fill='#374151'
                      fontWeight='500'
                    >
                      RESOLVED BY AI
                    </text>
                    <text
                      x='615'
                      y='92'
                      fontSize='13'
                      fill='#1F2937'
                      fontWeight='700'
                    >
                      3,330
                    </text>

                    <rect
                      x='560'
                      y='210'
                      width='100'
                      height='30'
                      fill='#9CA3AF'
                      rx='6'
                    />
                    <text
                      x='570'
                      y='223'
                      fontSize='10'
                      fill='#FFFFFF'
                      fontWeight='500'
                    >
                      SENT TO TEAMMATE
                    </text>
                    <text
                      x='570'
                      y='235'
                      fontSize='12'
                      fill='#FFFFFF'
                      fontWeight='700'
                    >
                      730
                    </text>

                    <rect
                      x='560'
                      y='250'
                      width='100'
                      height='30'
                      fill='#9CA3AF'
                      rx='6'
                    />
                    <text
                      x='570'
                      y='263'
                      fontSize='10'
                      fill='#FFFFFF'
                      fontWeight='500'
                    >
                      ABANDONED
                    </text>
                    <text
                      x='570'
                      y='275'
                      fontSize='12'
                      fill='#FFFFFF'
                      fontWeight='700'
                    >
                      82
                    </text>

                    <path
                      d='M 608 142 Q 750 142 900 100'
                      stroke='url(#flowGradient2)'
                      strokeWidth='60'
                      fill='none'
                      opacity='0.3'
                    />
                    <path
                      d='M 608 160 Q 750 170 900 155'
                      stroke='url(#flowGradient2)'
                      strokeWidth='10'
                      fill='none'
                      opacity='0.3'
                    />
                    <path
                      d='M 608 175 Q 750 190 900 195'
                      stroke='url(#flowGradient2)'
                      strokeWidth='8'
                      fill='none'
                      opacity='0.3'
                    />

                    {/* Stage 4: CX Ratings */}
                    <rect
                      x='900'
                      y='60'
                      width='8'
                      height='120'
                      fill='#8B5CF6'
                      rx='4'
                    />
                    <text
                      x='915'
                      y='75'
                      fontSize='11'
                      fill='#374151'
                      fontWeight='500'
                    >
                      POSITIVE CX RATINGS
                    </text>
                    <text
                      x='915'
                      y='92'
                      fontSize='13'
                      fill='#1F2937'
                      fontWeight='700'
                    >
                      2,730
                    </text>

                    <rect
                      x='1050'
                      y='145'
                      width='90'
                      height='30'
                      fill='#F59E0B'
                      rx='6'
                    />
                    <text
                      x='1060'
                      y='158'
                      fontSize='10'
                      fill='#FFFFFF'
                      fontWeight='500'
                    >
                      NEUTRAL CX
                    </text>
                    <text
                      x='1060'
                      y='170'
                      fontSize='12'
                      fill='#FFFFFF'
                      fontWeight='700'
                    >
                      330
                    </text>

                    <rect
                      x='1050'
                      y='185'
                      width='90'
                      height='30'
                      fill='#EF4444'
                      rx='6'
                    />
                    <text
                      x='1060'
                      y='198'
                      fontSize='10'
                      fill='#FFFFFF'
                      fontWeight='500'
                    >
                      NEGATIVE CX
                    </text>
                    <text
                      x='1060'
                      y='210'
                      fontSize='12'
                      fill='#FFFFFF'
                      fontWeight='700'
                    >
                      270
                    </text>
                  </StyledSVG>
                </FunnelContainer>
              </FunnelSection>
            </PerformanceCard>
          </ContentContainer>
        </MainContent>
      </div>
    </Tooltip.Provider>
  )
})

// Helps to identify component in React error logs
if (process.env.NODE_ENV !== 'production') {
  Performance.displayName = 'Performance'
}

export default Performance
